import { useCallback, useEffect, useRef, type RefObject } from 'react';

const HOLD_INITIAL_DELAY_MS = 450;
const HOLD_REPEAT_INTERVAL_MS = 90;

/**
 * Press-and-hold auto-repeat for a stepper button. `onFire` fires once per
 * tap/click via the browser's normal click handling (wired separately by the
 * caller) — this hook only adds the REPEAT behavior on top, starting after
 * `HOLD_INITIAL_DELAY_MS` so a quick tap never double-fires. `canFireRef` is
 * read fresh on every tick (not captured at press-start) so a mid-hold
 * change in affordability/slot availability stops the repeat immediately.
 */
export function useHoldRepeat(onFire: () => void, canFireRef: RefObject<boolean>) {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const clear = useCallback(() => {
        if (timeoutRef.current !== null) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
        if (intervalRef.current !== null) { clearInterval(intervalRef.current); intervalRef.current = null; }
    }, []);

    const start = useCallback(() => {
        clear();
        timeoutRef.current = setTimeout(() => {
            intervalRef.current = setInterval(() => {
                if (!canFireRef.current) { clear(); return; }
                onFire();
            }, HOLD_REPEAT_INTERVAL_MS);
        }, HOLD_INITIAL_DELAY_MS);
    }, [clear, onFire, canFireRef]);

    useEffect(() => clear, [clear]);

    return {
        onPointerDown: start,
        onPointerUp: clear,
        onPointerLeave: clear,
        onPointerCancel: clear,
    };
}
